import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: OrderTotalDisplayStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface OrderTotalDisplayStyles {
    container?: GridContainerProps;
    quoteSummaryLabelGridItem?: GridItemProps;
    quoteSummaryLabel?: TypographyProps;
    subtotalLabelGridItem?: GridItemProps;
    subtotalValueGridItem?: GridItemProps;
    subtotalValue?: TypographyProps;
}

export const orderTotalDisplayStyles: OrderTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            padding: 16px;
            box-shadow: 0 1px 4px 0 ${color("common.black", 0.2)};
            font-weight: bold;
            line-height: 26px;
            @media print {
                page-break-after: always;
            }
            ${xScreen("lte", "md")} {
                margin-top: 36px;
            }
        `,
    },
    quoteSummaryLabelGridItem: {
        width: 12,
        css: css`
            &&& {
                padding-bottom: 15px;
                margin-bottom: 12px;
                border-bottom: 1px solid ${color("common.ui6")};
            }
        `,
    },
    quoteSummaryLabel: {
        css: css`
            height: 21px;
            color: ${color("common.ui1")};
            font-size: 18px;
            font-weight: bold;
            line-height: 20px;
        `,
    },
    subtotalLabelGridItem: {
        width: 6,
    },
    subtotalValueGridItem: {
        css: css`
            @media print {
                text-align: right;
            }
        `,
        width: 6,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
            font-weight: normal;
        `,
    },
};

const OrderTotalDisplay: FC<Props> = ({ quoteState, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderTotalDisplayStyles, extendedStyles));

    const quote = quoteState.value;

    if (!quote) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.quoteSummaryLabelGridItem}>
                <Typography {...styles.quoteSummaryLabel}>{translate("Quote Summary")}</Typography>
            </GridItem>
            <GridItem {...styles.subtotalLabelGridItem}>
                <Typography>{translate("Subtotal")}</Typography>
            </GridItem>
            <GridItem {...styles.subtotalValueGridItem}>
                <Typography {...styles.subtotalValue} translate="no">{quote.orderSubTotalDisplay}</Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderTotalDisplay),
    definition: {
        displayName: "Rfq Total Display",
        allowedContexts: ["RfqQuoteDetailsPage"],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;
